import {
  Alert,
  ArrowRightIcon,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from '@lokalise/louis'
import type { Conflict } from '../../types/api'
import {
  StepContainer,
  StepContents,
  StepFooter,
  StepTitle,
  StepTitleContainer,
} from '../Step/Step'
import styles from './OverrideStep.module.css'

type OverrideStepProps = {
  conflicts: Conflict[]
  profileName: string
  isOverriding?: boolean
  onClose: () => void
  onUpdate: () => void
}

export const OverrideStep = ({
  onClose,
  onUpdate,
  isOverriding,
  profileName,
  conflicts,
}: OverrideStepProps) => (
  <div className={styles.container}>
    <StepContainer>
      <StepContents>
        <StepTitleContainer>
          <StepTitle>Update anyway - {profileName}</StepTitle>
        </StepTitleContainer>
        <div className="flex-col gap-7">
          <Alert status="warning" withStartAdornmentIcon>
            Some projects are already served by other AI profiles. See below for details on the
            changes that will occur and how they may impact your workflows.
          </Alert>
          <Table className={styles.table}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell align="center"> Project </TableHeaderCell>
                <TableHeaderCell align="center"> AI profile name </TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {formatConflicts(conflicts).map(({ project, profile }) => (
                <TableRow key={`${project}-${profile}`}>
                  <TableCell align="center">{project}</TableCell>
                  <TableCell
                    align="center"
                    aria-label={`Use ${profileName} profile instead of ${profile}`}
                  >
                    <div className="flex gap-2 justify-center">
                      <s>{profile}</s> <ArrowRightIcon size="20px" /> {profileName}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <StepFooter>
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={onUpdate}
            loading={isOverriding}
            disabled={isOverriding}
            variant="primary"
          >
            Update anyway
          </Button>
        </StepFooter>
      </StepContents>
    </StepContainer>
  </div>
)

const formatConflicts = (conflicts: Conflict[]) =>
  conflicts.flatMap((conflict) =>
    conflict.projects.map((project) => ({
      profile: conflict.name,
      project: project.name,
    })),
  )
